<template>
  <v-container fluid>
    <v-row ref="filtersContainer" class="mb-1">
      <v-col cols="6" class="pb-0">
        <span style="vertical-align:middle;" class="text-h5">Список приёмок</span>

        <template v-if="userService.can('priyomka_add_button')">
          <v-btn class="ml-3" style="vertical-align: middle;" rounded color="primary" small
                 @click="showForm()"
          >
            <v-icon left>mdi-plus</v-icon> Добавить
          </v-btn>
          <v-dialog v-model="formDialog" width="95vw" persistent>
            <transfer-form v-if="formDialog"
                           @transfer:form:saved="closeForm"
                           @transfer:form:cancel="closeForm"
            ></transfer-form>
          </v-dialog>
        </template>
      </v-col>
      <v-col cols="6" class="text-right font-weight-bold pb-0">
        <span class="purple--text text--darken-3">Всего в суммах: {{totals.uzs_summa||0|money}}</span>
        <span class="indigo--text text--darken-2 ml-3">Всего в валюте: {{totals.usd_summa|money('usd')}}</span>
      </v-col>

      <v-col cols="2">
        <v-text-field v-model="filter.reg_number" :disabled="!!fetching"
                      label="Номер регистрации" prepend-inner-icon="mdi-identifier"
                      ref="reg_number_filter"
                      clearable hide-details
        ></v-text-field>
      </v-col>
      <v-col cols="2">
        <v-menu v-model="fromDateMenu" transition="scale-transition" offset-y max-width="290px" min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field :value="fromDateFormatted"
                          label="с" prepend-inner-icon="mdi-calendar"
                          readonly v-bind="attrs" v-on="on" hide-details
            ></v-text-field>
          </template>
          <v-date-picker v-model="filter.reg_date_after" :disabled="!!fetching"></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="2">
        <v-menu v-model="toDateMenu" transition="scale-transition" offset-y max-width="290px" min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field :value="toDateFormatted"
                          label="по" prepend-inner-icon="mdi-calendar"
                          readonly v-bind="attrs" v-on="on" hide-details
            ></v-text-field>
          </template>
          <v-date-picker v-model="filter.reg_date_before" :disabled="!!fetching"></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="2">
        <v-autocomplete v-model="filter.counteragent" :items="counteragents"
                        item-value="id" item-text="full_name"
                        prepend-inner-icon="mdi-account-group" label="Контрагент"
                        :loading="!!counteragentsLoading" :disabled="!!counteragentsLoading"
                        clearable hide-details
        ></v-autocomplete>
      </v-col>
      <v-col cols="4">
        <v-autocomplete v-model="filter.raw_materials" :items="materials"
                        item-value="id" item-text="name"
                        prepend-inner-icon="mdi-package-variant" label="Материалы"
                        :loading="!!materialsLoading"
                        multiple clearable hide-details
        >
          <template v-slot:selection="{item, index}">
            <span v-if="index === 0" class="text-truncate" style="max-width: 60%">{{item.name}}</span>
            <span class="pl-1 grey--text text-caption" v-if="index === 1">
              и еще {{filter.raw_materials.length - 1}}
            </span>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>

    <v-data-table
      :headers="nonSortableHeaders"
      :items="transfers"
      :loading="!!fetching" :items-per-page="filter.limit"
      :height="dataTableHeight" disable-items-per-page hide-default-footer fixed-header
      no-data-text="Нет данных" loading-text="Данные загружаются"
    >
      <template #item="{item, index}">
        <tr>
          <td class="text-center">{{filter.limit * (filter.page - 1) + index + 1}}</td>
          <td>{{item.reg_number}}</td>
          <td>{{item.reg_date|date}}</td>
          <td>{{item.counteragent}}</td>
          <td class="text-right">{{item.total|money(item.currency)}}</td>
          <td>{{item.warehouse.name}}</td>
          <td class="text-right">
            <v-btn class="ml-2" text small depressed color="success" @click="showDetails(item)">
              <v-icon left small>mdi-eye</v-icon>
              Показать
            </v-btn>
            <v-btn class="ml-2" icon small depressed color="primary" dark @click="showForm(item)">
              <v-icon small>mdi-pen</v-icon>
            </v-btn>
            <v-btn class="ml-2" icon small depressed color="error" @click="askRemove(item)"
                   :loading="deleting[item.id]"
            >
              <v-icon left small>mdi-delete</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>

    <pagination ref="paginationContainer" :limit.sync="limit" :page.sync="page" :total-pages="totalPages"></pagination>

    <v-dialog v-model="detailDialog" width="95vw" persistent>
      <transfer-details @dialog:close="closeDetails"
                        @dialog:edit="editForm"
      ></transfer-details>
    </v-dialog>
  </v-container>
</template>

<script>
  import moment from 'moment'
  import {mapState as defaultMapState, mapGetters, createNamespacedHelpers} from 'vuex'
  const {mapState, mapActions, mapMutations} = createNamespacedHelpers('Transfer')

  import TransferDetails from '@/modules/transfer/details'
  import TransferForm from '@/modules/transfer/form'
  import {SERVER_DATE_FORMAT, USER_DATE_FORMAT} from '@/filters'
  import ut from '@/utils'

  import {hasFilter, hasDialog} from '@/mixins'

  export default {
    name: 'transfer-list',

    mixins: [hasFilter, hasDialog],

    customFilters: {
      reg_number: '',
      reg_date_after: moment().subtract(1, 'month').format(SERVER_DATE_FORMAT),
      reg_date_before: moment().format(SERVER_DATE_FORMAT),
      counteragent: null,
      // documents containing any of these raw materials (sent as raw_materials[])
      raw_materials: [],
    },

    created() {
      // from a link (?raw_materials=58) the query value arrives as a single number or strings
      this.filter.raw_materials = [].concat(this.filter.raw_materials || []).map(Number).filter(Boolean)

      if (this.$route.params.id) {
        this.showDetails(this.$route.params)
      }
      this.fetch(this.filter)
    },

    computed: {
      ...mapState([
        'fetching',
        'data',
        'deleting',
      ]),

      headers() {
        return [
          {text: '#', align: 'center', width: '100'},
          {text: 'Рег номер'},
          {text: 'Дата'},
          {text: 'Контрагент'},
          {text: 'Сумма', align: 'right'},
          {text: 'Склад'},
          {text: 'Действия', align: 'right', sortable: false},
        ]
      },

      transfers() {
        return this.data?.results || []
      },

      ...defaultMapState('Counteragent', {
        counteragents: state => state.filterData?.results || [],
        counteragentsLoading: state => !!state.fetching,
      }),

      ...defaultMapState('Material', {
        materials: state => state.data?.results || [],
        materialsLoading: state => !!state.fetching,
      }),

      toDateFormatted() {
        return moment(this.filter.reg_date_before).format(USER_DATE_FORMAT)
      },

      fromDateFormatted() {
        return moment(this.filter.reg_date_after).format(USER_DATE_FORMAT)
      },

      ...mapGetters('CurrencyRate', ['getCurrency']),

      totals() {
        return this.data?.total || {}
      },
    },

    methods: {
      ...mapActions(['fetch', 'show', 'remove']),
      ...mapMutations(['setModel', 'setTransferItems', 'createItem']),

      closeDetails() {
        this.setModel({})
        this.setTransferItems([])
        this.detailDialog = false
      },

      editForm(item) {
        this.showForm(item)
        this.detailDialog = false
      },

      showForm(item) {
        if (item && item.id) {
          this.show(item.id)
        } else {
          this.setModel({})
          this.createItem({})
        }
        this.formDialog = true
      },

      closeForm() {
        this.formDialog = false
        this.setModel({})
        this.setTransferItems([])
        this.fetch(this.filter)
      },
    },

    mounted() {
      this.$eventBus.$emit('setTitle', 'Список приёмок')
      this.$store.dispatch('Measurement/fetch')
      this.$store.dispatch('Counteragent/fetchFilterList')
      this.$store.dispatch('Material/fetch')
    },

    components: {
      TransferForm,
      TransferDetails,
    }
  }
</script>
